import type { InsightEvidenceGroup } from "~vue/Labs/evidence";
import type { PerspectiveResult, Question } from "~vue/pages/perspectiveTypes";
import type { ContextSnapshot } from "~vue/ProfileSetup/types";

/**
 * Wire types for the context-first onboarding flow (LAB-1306).
 *
 * Hand-written because these endpoints are Django views, not coach_api, so there
 * is no OpenAPI schema to generate from. They mirror the Pydantic models in
 * `vpoc/apps/onboarding/calendar_first/types.py` — change both together.
 */

export type OnboardingStep =
    | "identity"
    | "intro"
    | "context_rings"
    | "calendar_connect"
    | "scanning"
    | "calendar_payoff"
    | "assessment_intro"
    | "assessment"
    | "perspective_result"
    | "sharpened"
    | "profile_intro"
    | "profile"
    | "done";

/**
 * The flow in order. Mirrors `STEP_ORDER` in the Python service — the browser URL
 * points at a step and the run's cursor sits at another, so the client has to
 * compare positions, not just equality.
 */
export const STEP_ORDER: readonly OnboardingStep[] = [
    "identity",
    "intro",
    "context_rings",
    "calendar_connect",
    "scanning",
    "calendar_payoff",
    "assessment_intro",
    "assessment",
    "perspective_result",
    "sharpened",
    "profile_intro",
    "profile",
    "done",
];

export function stepIndex(step: OnboardingStep): number {
    return STEP_ORDER.indexOf(step);
}

export type ScanStatus = "not_started" | "syncing" | "stats_ready" | "failed" | "skipped";
/* `skipped` is terminal like `ready`: there was nothing to write the profile from,
   so the flow has no publish step to show. */
export type ProfileStatus = "not_started" | "running" | "ready" | "failed" | "skipped";
export type ReadingsStatus = "not_started" | "running" | "ready" | "failed" | "skipped";
export type AccessLevel = "private" | "invited_org_members" | "organization";
export type PerspectiveRevealStatus = "ready" | "unavailable";

export interface CalendarScanPerson {
    name: string;
    meeting_count: number;
    shared_hours: number;
    has_visible_profile: boolean;
}

export interface CalendarScanStats {
    window_days: number;
    provider_name: string;
    meeting_count: number;
    total_meeting_hours: number;
    hours_per_week: number;
    organized_count: number;
    attended_count: number;
    top_people: CalendarScanPerson[];
    profile_sharer_names: string[];
    recurring_series_count: number;
    recurring_occurrence_count: number;
    recurring_hours: number;
    busiest_weekday: string;
    back_to_back_count: number;
    sparse: boolean;
}

export interface OnboardingReading {
    /** The `LabInsight` row, so the card addresses the same insight My Work does. */
    id: string;
    /** Sets the card's category tier and colour, exactly as on My Work. */
    experiment_slug: string;
    category: string;
    title: string;
    summary: string;
    /** The reasoning, behind the card's "See more". */
    body: string;
    unknowns: string[];
    evidence_groups: InsightEvidenceGroup[];
}

export interface ReadingsState {
    status: ReadingsStatus;
    readings: OnboardingReading[];
    sparse: boolean;
}

export interface ScanState {
    status: ScanStatus;
    stats: CalendarScanStats | null;
    readings: ReadingsState | null;
}

export interface IdentityPrefill {
    first_name: string;
    last_name: string;
    role: string;
    source: string;
}

export interface CalendarContext {
    provider: string;
    provider_label: string;
    connected: boolean;
    auth_url_endpoint: string;
    /** The user's recorded timezone, empty when they have none. */
    timezone: string;
    timezones: string[];
}

export interface RunState {
    run_id: string;
    current_step: OnboardingStep;
    manages_people: boolean | null;
    calendar_skipped: boolean;
    assessment_skipped: boolean;
    publish_choice: AccessLevel | null;
    scan_status: ScanStatus;
    profile_status: ProfileStatus;
    calendar_stats: CalendarScanStats | null;
    identity_prefill: IdentityPrefill;
    calendar: CalendarContext;
    organization_name: string;
    available_access_levels: AccessLevel[];
    has_perspective_result: boolean;
    assessment_question_count: number;
    /** How well Nadia knows the user, scored server-side from the same artifacts as the four setup asks. */
    context: ContextSnapshot;
}

export interface ProfileSubtopicPreview {
    id: string;
    title: string;
    content: string;
    placeholder: string;
}

export interface ProfilePreview {
    status: ProfileStatus;
    name: string;
    role: string;
    subtopics: ProfileSubtopicPreview[];
    /** Whether the user had written their own profile content before this run generated over it. */
    had_existing_content: boolean;
}

/**
 * The archetype screen's payload. `result` is the same shape the standard
 * results page renders, so both screens read their copy out of
 * `pages/perspectiveTypes.ts` rather than out of two different payloads.
 */
export interface PerspectiveRevealState {
    status: PerspectiveRevealStatus;
    result: PerspectiveResult | null;
}

export type AssessmentQuestion = Question;

export interface AssessmentState {
    session_id: string;
    questions: AssessmentQuestion[];
    saved_responses: Record<string, number>;
}
